import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { CreateBudgetAllocation, UpdateBudgetAllocation } from '@repo/backend/crt'
import { BUDGET_ALLOCATIONS_CREATE_ERROR_TYPES } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const budgetAllocationsSearchSchema = z.object({
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export type BudgetAllocationsSearch = z.infer<typeof budgetAllocationsSearchSchema>

export type BudgetAllocations = Awaited<ReturnType<typeof getBudgetAllocations>>
export type BudgetAllocationListItem = BudgetAllocations['results'][number]

async function getBudgetAllocations(search: BudgetAllocationsSearch = {}, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['budget-allocations'].$get(
    { query: stringifyValues(search) },
    { init: { signal } },
  )
  return await res.json()
}

export function useBudgetAllocations(queries: BudgetAllocationsSearch = {}) {
  return useQuery({
    queryKey: ['budgetAllocations', queries],
    queryFn: ({ signal }) => getBudgetAllocations(queries, signal),
  })
}

async function getBudgetAllocationSummary(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['budget-allocations'].summary.$get({}, { init: { signal } })
  return await res.json()
}

export const budgetAllocationSummaryQueryOptions = {
  queryKey: ['budgetAllocationSummary'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getBudgetAllocationSummary(signal),
}

export function useBudgetAllocationSummary() {
  return useQuery(budgetAllocationSummaryQueryOptions)
}

async function getBudgetAllocationsForExport(
  queries: BudgetAllocationsSearch = {},
  signal?: AbortSignal,
) {
  const stringifiedQueries = stringifyValues(queries)
  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()
  return fetchCsvExport(`/crt/budget-allocations/export?${queryParams}`, signal)
}

export const budgetAllocationDetailsExportQueryOptions = (
  queries: BudgetAllocationsSearch = {},
) => ({
  queryKey: ['budgetAllocationDetailsExport', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getBudgetAllocationsForExport(queries, signal),
})

async function getBudgetAllocationsForRawExport(signal?: AbortSignal) {
  return fetchCsvExport('/crt/budget-allocations/raw-export', signal)
}

export const budgetAllocationDetailsRawExportQueryOptions = {
  queryKey: ['budgetAllocationDetailsRawExport'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getBudgetAllocationsForRawExport(signal),
}

async function createBudgetAllocation(payload: CreateBudgetAllocation) {
  const api = getApi()
  const res = await api.crt['budget-allocations'].$post({ json: payload })
  return await res.json()
}

async function updateBudgetAllocation(id: number, payload: UpdateBudgetAllocation) {
  const api = getApi()
  await api.crt['budget-allocations'][':id'].$patch({ param: { id: String(id) }, json: payload })
}

async function deleteBudgetAllocation(id: number) {
  const api = getApi()
  await api.crt['budget-allocations'][':id'].$delete({ param: { id: String(id) } })
}

async function bulkDeleteBudgetAllocations(ids: number[]) {
  const api = getApi()
  const res = await api.crt['budget-allocations'].bulk.$delete({ json: { ids } })
  return await res.json()
}

function useInvalidateBudgetAllocations() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['budgetAllocations'] })
    queryClient.invalidateQueries({ queryKey: ['budgetAllocationCodes'] })
    queryClient.invalidateQueries({ queryKey: ['budgetAllocationOptions'] })
    queryClient.invalidateQueries({ queryKey: ['budgetAllocationSummary'] })
  }
}

function getMutationErrorDescription(
  error: unknown,
  t: ReturnType<typeof useTranslation>['t'],
  fallbackKey: string,
): string {
  const issues = extractZodIssues(error)
  const customParams = issues?.find((i) => i.code === 'custom')?.params as
    | { type?: string; budgetAllocation?: string }
    | undefined

  if (customParams?.type === BUDGET_ALLOCATIONS_CREATE_ERROR_TYPES.budgetAllocationConflict) {
    return t('errors.budgetAllocations.budgetAllocationConflict', {
      ns: 'crt',
      budgetAllocation: customParams.budgetAllocation,
    })
  }

  return getFirstZodErrorMessage(issues, t, fallbackKey, 'crt')
}

export function useCreateBudgetAllocation(handlers?: QueryHandlers) {
  const { t } = useTranslation(['crt', 'common'])
  const invalidate = useInvalidateBudgetAllocations()

  return useMutation({
    mutationKey: ['createBudgetAllocation'],
    mutationFn: createBudgetAllocation,
    onSuccess: () => {
      invalidate()
      handlers?.handleOnSuccess?.()
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('budgetAllocations.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: getMutationErrorDescription(error, t, 'errors.budgetAllocations.createFailed'),
        className: toastClassName,
      })
    },
  })
}

export function useUpdateBudgetAllocation(args: QueryHandlers & { budgetAllocationId: number }) {
  const { t } = useTranslation(['crt', 'common'])
  const invalidate = useInvalidateBudgetAllocations()

  return useMutation({
    mutationKey: ['updateBudgetAllocation', args.budgetAllocationId],
    mutationFn: (payload: UpdateBudgetAllocation) =>
      updateBudgetAllocation(args.budgetAllocationId, payload),
    onSuccess: () => {
      invalidate()
      args.handleOnSuccess?.()
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('budgetAllocations.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: getMutationErrorDescription(error, t, 'errors.budgetAllocations.updateFailed'),
        className: toastClassName,
      })
    },
  })
}

export function useDeleteBudgetAllocation(args?: QueryHandlers) {
  const { t } = useTranslation(['crt', 'common'])
  const invalidate = useInvalidateBudgetAllocations()

  return useMutation({
    mutationKey: ['deleteBudgetAllocation'],
    mutationFn: (id: number) => deleteBudgetAllocation(id),
    onSuccess: () => {
      invalidate()
      args?.handleOnSuccess?.()
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('budgetAllocations.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: getFirstZodErrorMessage(
          extractZodIssues(error),
          t,
          'errors.budgetAllocations.deleteFailed',
          'crt',
        ),
        className: toastClassName,
      })
    },
  })
}

export function useBulkDeleteBudgetAllocations(args?: QueryHandlers) {
  const { t } = useTranslation(['crt', 'common'])
  const invalidate = useInvalidateBudgetAllocations()

  return useMutation({
    mutationKey: ['bulkDeleteBudgetAllocations'],
    mutationFn: (ids: number[]) => bulkDeleteBudgetAllocations(ids),
    onSuccess: () => {
      invalidate()
      args?.handleOnSuccess?.()
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('budgetAllocations.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      toast.error(t('toast.error', { ns: 'common' }), {
        description: getFirstZodErrorMessage(
          extractZodIssues(error),
          t,
          'errors.budgetAllocations.deleteFailed',
          'crt',
        ),
        className: toastClassName,
      })
    },
  })
}
